import { FC } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { useSignal, useSignalEffect } from "@preact/signals";
import { useApp } from "~/core";
import { animator } from "~/chart";
import { FullscreenDialog } from "~/components";
import { MobileImageSliderProps } from "./interfaces";
import cn from "classnames";

const GAP_X = 20;
const MAX_SCALE = 3;
const MIN_SCALE = 1;
const MAX_OVERSCALE = 0.1;
const MAX_OVERSCROLL = 20;

const stubTranslateAnim = animator.anim(0, 300);

const MobileImageSlider: FC<MobileImageSliderProps> = ({
    slides,
    show,
    activeIndex,
}) => {
    const { mobile } = useApp();
    const cleanSignal = useSignal(0);
    const isZooming = useSignal(false);
    const hideClose = useSignal(false);
    const translateX = useSignal(
        activeIndex.peek() * -(window.innerWidth + GAP_X),
    );
    const translateSmooth = useRef(false);
    const translateAnimRef = useRef(stubTranslateAnim);

    useEffect(() => {
        const translateAnim = animator.anim(0, 300);
        const scaleAnim = animator.anim(1, 100);
        const panXAnim = animator.anim(0, 100);
        const panYAnim = animator.anim(0, 100);
        let startX = -1;
        let initialTranslate = 0;
        let startDist = 0;
        let centerX = 0;
        let centerY = 0;
        let scale = 1;
        let panX = 0;
        let panY = 0;
        let startTouchX = 0;
        let startTouchY = 0;
        let startPanX = 0;
        let startPanY = 0;
        let lastScale = 1;
        let ignore = false;
        let zoomSlide: HTMLElement | null = null;

        const getSlidesCount = () => {
            const element =
                document.querySelector<HTMLDivElement>("[data-mis-window]");

            if (!element) {
                return 0;
            }

            return Number(element.getAttribute("data-mis-window"));
        };

        const updateZoom = () => {
            if (!zoomSlide) {
                return;
            }

            zoomSlide.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`;
        };

        const cleanupZoom = () => {
            zoomSlide?.style.removeProperty("transform");
            zoomSlide = null;
            scale = 1;
            panX = 0;
            panY = 0;
            startTouchX = 0;
            startTouchY = 0;
            startPanX = 0;
            startPanY = 0;
            lastScale = 1;
        };

        const setPan = (x: number, y: number) => {
            if (!zoomSlide) {
                return;
            }

            const rect = zoomSlide.getBoundingClientRect();
            const ih = rect.height / scale;
            const dih = window.innerHeight - ih;
            const dx = Math.abs(window.innerWidth - rect.width);
            const dy = Math.abs(window.innerHeight - rect.height);
            const maxX = dx / 2 + MAX_OVERSCROLL;
            const maxY = (dy + dih) / 2 + MAX_OVERSCROLL;

            panX = Math.min(maxX, Math.max(-maxX, x));
            panY = Math.min(maxY, Math.max(-maxY, y));
        };

        const onTouchStart = (e: TouchEvent) => {
            if (!show.peek()) {
                return;
            }

            const button =
                e.target instanceof Element
                    ? e.target.closest("button, a, [role=button]")
                    : null;

            if (button) {
                ignore = true;
                return;
            }

            const slide =
                e.target instanceof Element
                    ? e.target.closest<HTMLElement>("[data-slide]")
                    : null;

            hideClose.value = scale !== 1 || e.touches.length > 1;

            if (
                e.touches.length === 2 &&
                slide &&
                (!zoomSlide || zoomSlide === slide)
            ) {
                e.preventDefault();

                isZooming.value = true;
                zoomSlide = slide;
                startX = -1;
                centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
                centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
                startDist = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY,
                );
                scaleAnim.stop();
            } else if (e.touches.length === 1 && scale === 1) {
                translateAnim.stop();
                initialTranslate = translateX.peek();
                startX = e.touches[0].clientX;
            } else if (e.touches.length === 1) {
                e.preventDefault();

                startTouchX = e.touches[0].clientX;
                startTouchY = e.touches[0].clientY;
                startPanX = panX;
                startPanY = panY;
                panXAnim.stop();
                panYAnim.stop();
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!show.peek() || ignore) {
                return;
            }

            if (e.touches.length === 2 && zoomSlide) {
                e.preventDefault();

                const dist = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY,
                );

                scale = Math.min(
                    MAX_SCALE + MAX_OVERSCALE,
                    Math.max(
                        MIN_SCALE - MAX_OVERSCALE,
                        (dist / startDist) * lastScale,
                    ),
                );

                const newCenterX =
                    (e.touches[0].clientX + e.touches[1].clientX) / 2;
                const newCenterY =
                    (e.touches[0].clientY + e.touches[1].clientY) / 2;

                setPan(
                    panX + (newCenterX - centerX),
                    panY + (newCenterY - centerY),
                );

                centerX = newCenterX;
                centerY = newCenterY;

                updateZoom();
            } else if (e.touches.length === 1 && scale !== 1) {
                e.preventDefault();

                const touch = e.touches[0];
                const f = 0.82;

                const dx = (touch.clientX - startTouchX) * f;
                const dy = (touch.clientY - startTouchY) * f;

                setPan(startPanX + dx, startPanY + dy);

                updateZoom();
            } else if (e.touches.length === 1 && startX !== -1) {
                const touch = e.touches[0];
                const delta = touch.clientX - startX;
                const newIndex = activeIndex.peek() + (delta > 0 ? -1 : 1);
                const factor =
                    newIndex < 0 || newIndex >= getSlidesCount() ? 0.5 : 1;

                translateX.value = initialTranslate + delta * factor;
            }
        };

        const onTouchEnd = (e: TouchEvent) => {
            if (!show.peek()) {
                return;
            }

            const ignored = ignore;

            if (e.touches.length === 0) {
                hideClose.value = false;
                ignore = false;
            }

            if (ignored) {
                return;
            }

            if (scale !== 1) {
                if (e.touches.length === 1) {
                    const touch = e.touches[0];

                    startTouchX = touch.clientX;
                    startTouchY = touch.clientY;
                    startPanX = panX;
                    startPanY = panY;
                    panXAnim.stop();
                    panYAnim.stop();
                } else if (e.touches.length === 0) {
                    const finalScale = Math.min(
                        MAX_SCALE,
                        Math.max(MIN_SCALE, scale),
                    );

                    if (finalScale !== scale) {
                        panXAnim.set(panX);
                        panYAnim.set(panY);
                        scaleAnim.set(scale).play(finalScale);
                        mobile.impact("light");

                        if (finalScale === 1) {
                            panXAnim.play(0);
                            panYAnim.play(0);
                        }
                    }

                    if (scale === 1) {
                        cleanupZoom();
                    }

                    lastScale = finalScale;
                    isZooming.value = finalScale !== 1;
                }
            } else if (startX !== -1) {
                const touch = e.changedTouches[0];
                const delta = touch.clientX - startX;
                const slidesCount = getSlidesCount();
                const newIndex = activeIndex.peek() + (delta > 0 ? -1 : 1);

                if (
                    Math.abs(delta) > window.innerWidth / 4 &&
                    newIndex >= 0 &&
                    newIndex < slidesCount
                ) {
                    translateSmooth.current = true;
                    activeIndex.value = newIndex;
                } else {
                    translateAnim
                        .set(translateX.peek())
                        .play(
                            -(window.innerWidth + GAP_X) * activeIndex.peek(),
                        );
                }

                startX = -1;
            }
        };

        window.addEventListener("touchstart", onTouchStart, { passive: false });
        window.addEventListener("touchmove", onTouchMove, { passive: false });
        window.addEventListener("touchend", onTouchEnd, { passive: false });
        window.addEventListener("touchcancel", onTouchEnd, { passive: false });
        translateAnimRef.current = translateAnim;

        const onAnimUpdate = () => {
            scale = scaleAnim.value;
            panX = panXAnim.value;
            panY = panYAnim.value;
            updateZoom();
        };

        translateAnim.onUpdate(() => (translateX.value = translateAnim.value));
        scaleAnim.onUpdate(onAnimUpdate);
        panXAnim.onUpdate(onAnimUpdate);
        panYAnim.onUpdate(onAnimUpdate);
        scaleAnim.onEnd = () => {
            if (scale === 1) {
                cleanupZoom();
            }
        };
        cleanSignal.subscribe(() => {
            cleanupZoom();
        });

        return () => {
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("touchend", onTouchEnd);
            window.removeEventListener("touchcancel", onTouchEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useSignalEffect(() => {
        const idx = activeIndex.value;
        const tx = idx * -(window.innerWidth + GAP_X);
        const current = translateX.peek();
        const translateAnim = translateAnimRef.current;

        if (
            translateAnim.toValue === tx ||
            (translateAnim.isFinished && current === tx)
        ) {
            return;
        }

        if (translateSmooth.current) {
            translateSmooth.current = false;
            translateAnim.set(current).play(tx);
        } else {
            translateX.value = tx;
            translateAnim.set(tx);
        }
    });

    useSignalEffect(() => {
        const active = isZooming.value;

        mobile.toggleStatusBar(!active);
    });

    const slideWidth = window.innerWidth;
    const fullWidth = slideWidth * slides.length + GAP_X * (slides.length - 1);

    return (
        <FullscreenDialog
            show={show}
            title="Attachments"
            onClose={() => {
                show.value = false;
                isZooming.value = false;
                hideClose.value = false;
            }}
            hideTitle={isZooming.value}
            hideClose={hideClose.value}
            beforeEnter={() => cleanSignal.value++}
        >
            <div className="relative h-[70vh] max-h-[70vh] max-w-full">
                <div
                    data-mis-window={slides.length}
                    style={{
                        width: fullWidth,
                        transform: `translateX(${translateX.value}px)`,
                        columnGap: `${GAP_X}px`,
                    }}
                    className="absolute left-0 top-0 flex h-full items-center"
                >
                    {slides.map((slide) => (
                        <div
                            key={slide.id}
                            data-slide={slide.id}
                            style={{ width: slideWidth }}
                        >
                            <img
                                src={slide.thumbnail}
                                className="object-cover"
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div
                className={cn(
                    "absolute bottom-4 left-0 flex w-full items-center justify-center gap-x-2 transition duration-200",
                    isZooming.value && "opacity-0",
                )}
            >
                {slides.map((slide, idx) => (
                    <button
                        key={slide.id}
                        onClick={() => {
                            translateSmooth.current = true;
                            activeIndex.value = idx;
                        }}
                    >
                        <img
                            src={slide.thumbnail}
                            alt=""
                            className={cn(
                                "max-h-8 max-w-8 rounded border-2 transition duration-100",
                                activeIndex.value === idx
                                    ? "border-sky-500"
                                    : "border-gray-300 opacity-60 hover:opacity-100",
                            )}
                        />
                    </button>
                ))}
            </div>
        </FullscreenDialog>
    );
};

export default MobileImageSlider;
